import { yupResolver } from '@hookform/resolvers/yup';
import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Logo from '../../assets/header/login/img/logo.png';
import LogoTo from '../../assets/header/login/img/logo-2.png';
import {
  FullnameModal,
  LicenseModal,
  TimeZoneModal,
} from '../../components/Modals';
import { ErrorMessage } from '../../components/Shared';
import {
  DEFAULT_IANA_TIME_ZONE,
  ROUTE_MAP,
  VALIDATION_SCHEMAS,
} from '../../constants';
import { useAuthContext } from '../../hooks';

import './css/app.css';
import './css/vendor.css';

export const Login = () => {
  const navigate = useNavigate();
  const { setUserRole, setRecordingVisible } = useAuthContext();
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [isTimeZoneModalOpen, setIsTimeZoneModalOpen] = useState(false);
  const [isFullnameModalOpen, setIsFullnameModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openLicenseModal = () => setIsLicenseModalOpen(true);
  const closeLicenseModal = () => setIsLicenseModalOpen(false);
  const openTimeZoneModal = () => setIsTimeZoneModalOpen(true);
  const closeTimeZoneModal = () => setIsTimeZoneModalOpen(false);
  const openFullnameModal = () => setIsFullnameModalOpen(true);
  const closeFullnameModal = () => setIsFullnameModalOpen(false);

  const { control, handleSubmit, getValues } = useForm({
    resolver: yupResolver(VALIDATION_SCHEMAS.LOGIN),
    mode: 'onChange',
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSignIn = handleSubmit(async formData => {
    setIsSubmitting(true);

    try {
      const response = await fetch('/signin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      setIsSubmitting(false);

      if (response.status === 403) {
        const { error } = await response.json();

        if (error === 'Accept the license agreement first') {
          openLicenseModal();
          return;
        }

        if (error === 'Please choose your location') {
          openTimeZoneModal();
          return;
        }

        if (error === 'Please fill in your full name to proceed') {
          openFullnameModal();
          return;
        }
      }

      if (response.status === 200 || response.status === 302) {
        const ianaTimeZone = Cookies.get('ianaTimeZone');
        moment.tz.setDefault(ianaTimeZone ?? DEFAULT_IANA_TIME_ZONE);
        const role = Cookies.get('role');
        setUserRole(role);
        const recordingVisible = Cookies.get('recordingVisible') === 'true';
        setRecordingVisible(recordingVisible);
        navigate(ROUTE_MAP.DASHBOARD, { replace: true });
        return;
      }

      if (response.status === 401) {
        toast.error('Invalid username or password', {
          position: 'top-right',
          autoClose: 3000,
        });
        return;
      }

      throw new Error();
    } catch {
      setIsSubmitting(false);
      if (isLicenseModalOpen) closeLicenseModal();
      if (isTimeZoneModalOpen) closeTimeZoneModal();
      if (isFullnameModalOpen) closeFullnameModal();
      toast.error('Login failed', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  });

  return (
    <div>
      <nav className="header" id="header">
        <div className="c-grid-fluid">
          <div className="header__wrapper">
            <div>
              <div className="logo logo--header">
                <a className="logo__link" href="/">
                  <img className="logo__image" src={Logo} alt="Logotype" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <main className="main main--start p-sign-in">
        <section className="sign" id="sign">
          <div className="c-grid">
            <div className="section__wrapper">
              <div className="c-section" data-align="center">
                <div className="c-section__head">
                  <div className="logo logo--header">
                    <a className="logo__link" href="/">
                      <img
                        className="logo__image"
                        src={LogoTo}
                        alt="Logotype"
                      />
                    </a>
                  </div>
                </div>
                <div className="c-section__body">
                  <h5 className="c-section__title">Log in to your account</h5>

                  <form
                    className="c-form"
                    autoComplete="off"
                    onSubmit={onSignIn}
                  >
                    <div className="c-form__field">
                      <label className="c-form__label" htmlFor="email">
                        Email
                      </label>
                      <Controller
                        control={control}
                        name="username"
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => {
                          const hasErrorMessage = !!error?.message;
                          return (
                            <>
                              <input
                                value={value}
                                onChange={onChange}
                                className="c-form__input"
                                id="email"
                                placeholder="Enter your email address"
                                autoComplete="username"
                              />
                              {hasErrorMessage && (
                                <ErrorMessage errorMessage={error.message} />
                              )}
                            </>
                          );
                        }}
                      />
                    </div>
                    <div className="c-form__field">
                      <label className="c-form__label" htmlFor="password">
                        Password
                      </label>
                      <Controller
                        control={control}
                        name="password"
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => {
                          const hasErrorMessage = !!error?.message;
                          return (
                            <>
                              <input
                                value={value}
                                onChange={onChange}
                                className="c-form__input"
                                type="password"
                                id="password"
                                placeholder="Enter your password..."
                                autoComplete="current-password"
                              />
                              {hasErrorMessage && (
                                <ErrorMessage errorMessage={error.message} />
                              )}
                            </>
                          );
                        }}
                      />
                    </div>
                    <div className="c-form__field c-form__field--btn">
                      <div className="c-btn__wrapper">
                        <input
                          className="c-btn"
                          type={'submit'}
                          data-btn-color="blue"
                          data-btn-size="full"
                          data-btn-fw="600"
                          data-btn-round="half"
                          value={'Log in'}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </form>
                  <p className="flex flex-col items-end p-2">
                    <Link to={ROUTE_MAP.FORGOT_PASSWORD}>Forgot Password?</Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <footer className="footer" id="footer">
        <div className="c-grid">
          <div className="footer__wrapper"></div>
        </div>
      </footer>
      {isLicenseModalOpen && (
        <LicenseModal
          open={isLicenseModalOpen}
          credentials={getValues()}
          onSignIn={onSignIn}
          onClose={closeLicenseModal}
        />
      )}
      {isTimeZoneModalOpen && (
        <TimeZoneModal
          open={isTimeZoneModalOpen}
          credentials={getValues()}
          onSignIn={onSignIn}
          onClose={closeTimeZoneModal}
        />
      )}
      {isFullnameModalOpen && (
        <FullnameModal
          open={isFullnameModalOpen}
          credentials={getValues()}
          onSignIn={onSignIn}
          onClose={closeFullnameModal}
        />
      )}
    </div>
  );
};
